import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  BarChart3, TrendingUp, Users, GraduationCap, Target, CalendarCheck, Wallet,
  Siren, ClipboardList, Award, ArrowUpRight, ArrowDownRight, Minus, DollarSign, Receipt, FileText,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Area, ComposedChart,
  BarChart, Bar, Cell, PieChart, Pie, Legend,
} from "recharts";
import { toast } from "sonner";
import AttendanceHeatmap from "@/components/AttendanceHeatmap";

const fmt = (n) => (Number(n) || 0).toFixed(3);

function Kpi({ label, value, sub, icon: Icon, tone = "sky", trend, testid }) {
  const tones = {
    sky: "from-sky-50 to-sky-100/40 text-sky-700 border-sky-200",
    emerald: "from-emerald-50 to-emerald-100/40 text-emerald-700 border-emerald-200",
    amber: "from-amber-50 to-amber-100/40 text-amber-700 border-amber-200",
    rose: "from-rose-50 to-rose-100/40 text-rose-700 border-rose-200",
    slate: "from-slate-50 to-slate-100/40 text-slate-700 border-slate-200",
    violet: "from-violet-50 to-violet-100/40 text-violet-700 border-violet-200",
  };
  return (
    <Card className={`bg-gradient-to-br border ${tones[tone]}`} data-testid={testid}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="text-[11px] font-black uppercase tracking-wider opacity-70">{label}</div>
          <Icon className="w-5 h-5 opacity-60" />
        </div>
        <div className="text-3xl font-black text-slate-900 mt-1">{value}</div>
        {sub && <div className="text-xs font-bold opacity-70 mt-1">{sub}</div>}
        {trend !== undefined && trend !== null && (
          <div className={`inline-flex items-center gap-1 text-[11px] font-bold mt-2 rounded-full px-2 py-0.5 ${
            trend > 0 ? "bg-emerald-100 text-emerald-700" : trend < 0 ? "bg-rose-100 text-rose-700" : "bg-slate-100 text-slate-500"
          }`}>
            {trend === 0 ? <Minus className="w-3 h-3" /> : trend > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {trend > 0 ? "+" : ""}{trend}% مقارنة بالشهر السابق
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [range, setRange] = useState("year"); // "month" or "year"
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/analytics/dashboard").then((r) => setData(r.data)).catch(() => toast.error("تعذر تحميل الإحصائيات"));
  }, []);

  const downloadFinancial = async () => {
    setDownloading(true);
    try {
      let url = "/financial-report.pdf";
      if (range === "month") {
        url += `?month=${new Date().toISOString().slice(0, 7)}`;
      } else if (range === "year") {
        const now = new Date();
        const start = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
        url += `?year_start=${start}`;
      }
      const r = await api.get(url, { responseType: "blob" });
      const blob = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blob;
      a.download = `التقرير-المالي.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(blob), 5000);
      toast.success("تم تصدير التقرير المالي");
    } catch (e) { toast.error(extractError(e)); }
    finally { setDownloading(false); }
  };

  if (!data) return <div className="text-center py-16 text-slate-400">جاري التحميل...</div>;

  const attTrend = data.attendance.rate_this_month - data.attendance.rate_last_month;
  const revTrend = data.financials.paid_last_month
    ? Math.round(((data.financials.paid_this_month - data.financials.paid_last_month) / data.financials.paid_last_month) * 100)
    : (data.financials.paid_this_month > 0 ? 100 : 0);

  const iepPieData = [
    { name: "مكتملة", value: data.iep.completed, color: "#10B981" },
    { name: "قيد التنفيذ", value: data.iep.in_progress, color: "#F59E0B" },
    { name: "لم تبدأ", value: data.iep.not_started, color: "#94A3B8" },
  ].filter((x) => x.value > 0);

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-sky-600" />
            لوحة تحليلات الإدارة
          </h1>
          <p className="text-slate-500 mt-2">مؤشرات أداء شاملة للمركز — {new Date(data.generated_at).toLocaleString("ar-EG")}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex bg-white border border-slate-200 rounded-xl p-1">
            <button
              onClick={() => setRange("month")}
              data-testid="range-month"
              className={`px-4 py-2 rounded-lg text-sm font-bold ${range === "month" ? "bg-sky-600 text-white" : "text-slate-600"}`}
            >
              الشهر الحالي
            </button>
            <button
              onClick={() => setRange("year")}
              data-testid="range-year"
              className={`px-4 py-2 rounded-lg text-sm font-bold ${range === "year" ? "bg-sky-600 text-white" : "text-slate-600"}`}
            >
              العام الدراسي
            </button>
          </div>
          <Button onClick={downloadFinancial} disabled={downloading} className="h-12 gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="download-financial-btn">
            <FileText className="w-4 h-4" /> {downloading ? "..." : "تقرير مالي PDF"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="إجمالي الأطفال" value={data.children_total} icon={Users} tone="sky" testid="kpi-children" />
        <Kpi label="متوسط الحضور 30 يوم" value={`${data.attendance.rate_30d}%`} sub={`الشهر: ${data.attendance.rate_this_month}%`} icon={CalendarCheck} tone="emerald" trend={attTrend} testid="kpi-attendance" />
        <Kpi label="متوسط تقدم IEP" value={`${data.iep.avg_progress}%`} sub={`${data.iep.total_goals} هدف · ${data.iep.completed} مكتمل`} icon={Target} tone="violet" testid="kpi-iep" />
        <Kpi label="متوسط التقييمات" value={`${data.evaluations.avg_percent}%`} sub={`${data.evaluations.total} تقييم · ${data.evaluations.children_covered} طفل`} icon={ClipboardList} tone="amber" testid="kpi-evaluations" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="الدخل هذا الشهر" value={`${fmt(data.financials.paid_this_month)}`} sub={`${data.currency} · مدفوعة`} icon={DollarSign} tone="emerald" trend={revTrend} testid="kpi-revenue-this" />
        <Kpi label="قيد التحصيل" value={`${fmt(data.financials.by_status.pending.total)}`} sub={`${data.currency} · ${data.financials.by_status.pending.count} فاتورة`} icon={Wallet} tone="amber" testid="kpi-pending" />
        <Kpi label="طوارئ 30 يوم" value={data.emergencies_30d} icon={Siren} tone="rose" testid="kpi-emergencies" />
        <Kpi label="عدد المعلمين" value={data.teachers.length} icon={GraduationCap} tone="slate" testid="kpi-teachers" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 card-elevated border-slate-200/70">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-lg font-black text-slate-900">مسار الحضور — آخر ١٤ يوم</div>
                <div className="text-xs text-slate-500 mt-0.5">نسبة الحضور اليومية</div>
              </div>
              <TrendingUp className="w-6 h-6 text-sky-600" />
            </div>
            <div className="h-64" dir="ltr">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={data.attendance.daily_trend} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                  <defs>
                    <linearGradient id="gAtt" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="date" tick={{ fill: "#94A3B8", fontSize: 10 }} tickFormatter={(d) => d?.slice(5) || ""} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#94A3B8", fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
                  <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} formatter={(v, k) => k === "rate" ? [`${v}%`, "نسبة الحضور"] : [v, k]} />
                  <Area dataKey="rate" fill="url(#gAtt)" stroke="none" />
                  <Line type="monotone" dataKey="rate" stroke="#10B981" strokeWidth={2.5} dot={{ r: 3.5, fill: "#10B981", stroke: "white", strokeWidth: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-slate-200/70">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="text-lg font-black text-slate-900">توزيع أهداف IEP</div>
              <Target className="w-5 h-5 text-violet-600" />
            </div>
            {iepPieData.length > 0 ? (
              <div className="h-56" dir="ltr">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={iepPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} label={(e) => `${e.value}`}>
                      {iepPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="text-center py-16 text-slate-400 text-sm">لا توجد أهداف مسجلة</div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="card-elevated border-slate-200/70">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-lg font-black text-slate-900">مسار الدخل — آخر ٦ أشهر</div>
              <div className="text-xs text-slate-500 mt-0.5">مدفوعات محصّلة بالشهر ({data.currency})</div>
            </div>
            <Receipt className="w-6 h-6 text-emerald-600" />
          </div>
          <div className="h-64" dir="ltr">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.financials.revenue_trend} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
                <XAxis dataKey="month" tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <YAxis tick={{ fill: "#94A3B8", fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 10, fontSize: 12 }} formatter={(v) => [`${fmt(v)} ${data.currency}`, "الدخل"]} />
                <Bar dataKey="revenue" radius={[8, 8, 0, 0]}>
                  {data.financials.revenue_trend.map((_, i, arr) => (
                    <Cell key={i} fill={i === arr.length - 1 ? "#059669" : "#10B98180"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <AttendanceHeatmap />

      <Card className="card-elevated border-slate-200/70">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-lg font-black text-slate-900">أداء المعلمين — آخر ٣٠ يوم</div>
              <div className="text-xs text-slate-500 mt-0.5">مرتّبون حسب النشاط الإجمالي</div>
            </div>
            <Award className="w-6 h-6 text-amber-600" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-[11px] text-slate-500 uppercase tracking-wider">
                  <th className="py-2 text-right font-bold">المعلم</th>
                  <th className="py-2 text-center font-bold">الأطفال</th>
                  <th className="py-2 text-center font-bold">سجلات حضور</th>
                  <th className="py-2 text-center font-bold">تقارير يومية</th>
                  <th className="py-2 text-center font-bold">تقييمات إجمالي</th>
                </tr>
              </thead>
              <tbody>
                {data.teachers.length === 0 ? (
                  <tr><td colSpan={5} className="py-8 text-center text-slate-400">لا يوجد معلمون</td></tr>
                ) : data.teachers.map((t, i) => (
                  <tr key={t.id} data-testid={`teacher-row-${t.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        {i === 0 && <span className="text-xs font-black bg-amber-100 text-amber-800 rounded-full px-1.5 py-0.5">⭐</span>}
                        <div>
                          <div className="font-bold text-slate-900">{t.name}</div>
                          {t.specialty && <div className="text-[11px] text-slate-500">{t.specialty}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 text-center font-bold text-slate-700">{t.children}</td>
                    <td className="py-3 text-center">
                      <span className="inline-block bg-sky-50 text-sky-700 rounded-full px-2 py-0.5 text-xs font-black">{t.attendance_records_30d}</span>
                    </td>
                    <td className="py-3 text-center">
                      <span className="inline-block bg-emerald-50 text-emerald-700 rounded-full px-2 py-0.5 text-xs font-black">{t.reports_30d}</span>
                    </td>
                    <td className="py-3 text-center">
                      <span className="inline-block bg-violet-50 text-violet-700 rounded-full px-2 py-0.5 text-xs font-black">{t.evaluations_total}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
